import { Dispatch } from 'react-redux';
import { Server } from './constants';
import { StoreState } from '../../reducers';
import { Api } from '../../typings/api';
import { Action } from '../../actions';
import { MultiAppAuthenticationCredentials, AspConnexionStatus, initialCredentials } from './reducer';
import LocalStorageManager from '../../utils/localStorage/manager';
import ApiUtils from '../../utils/api';
import { receiveConfig } from '../Gallery/actions';
import UserAgentUtils from '../../utils/userAgent';
import { createNotification } from '../NotificationBar/actions';
import { AuthMethod } from './constants';
import Utils, { ServerStatusCode } from '../../utils';
import UrlUtils from '../../utils/url';

export enum MultiAppAuthenticationInfosActionKeys {
	RESET_STATUS = '@@multiAppAuthenticationInfos/RESET_STATUS',
	LOGIN_SUCCESS = '@@multiAppAuthenticationInfos/LOGIN_SUCCESS',
	LOGIN_ERROR = '@@multiAppAuthenticationInfos/LOGIN_ERROR',
	COMPANY_ERROR = '@@multiAppAuthenticationInfos/COMPANY_ERROR',
	LOGOUT = '@@multiAppAuthenticationInfos/LOGOUT',
	UPDATE_CLIENT = '@@multiAppAuthenticationInfos/UPDATE_CLIENT',
	UPDATE_CREDENTIALS = '@@multiAppAuthenticationInfos/UPDATE_CREDENTIALS',
	UPDATE_ACCESSIBLE_APPS = '@@multiAppAuthenticationInfos/UPDATE_ACCESSIBLE_APPS',
	UPDATE_AUTH_METHODS = '@@multiAppAuthenticationInfos/UPDATE_AUTH_METHODS',
	UPDATE_SERVER = '@@multiAppAuthenticationInfos/UPDATE_SERVER',
	SET_FORM_VALUE = '@@multiAppAuthenticationInfos/SET_FORM_VALUE',
	SET_SESSION_CHECKING = '@@multiAppAuthenticationInfos/SET_SESSION_CHECKING',
	TOGGLE_CHECK_SESSION_STATUS= '@@multiAppAuthenticationInfos/TOGGLE_CHECK_SESSION_STATUS',
	SET_ANOTHER_USER_LOGIN = '@@multiAppAuthenticationInfos/SET_ANOTHER_USER_LOGIN',
}

interface ResetLoginStatusAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.RESET_STATUS;
}

interface SetNoCompanyStatusAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.COMPANY_ERROR;
}

interface LoginSuccessfullAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.LOGIN_SUCCESS;
}

interface LoginFailedAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.LOGIN_ERROR;
	status: AspConnexionStatus;
}

interface LogoutAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.LOGOUT;
}

interface UpdateClientAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.UPDATE_CLIENT;
	clientId: number;
}

interface UpdateAccessibleAppsAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.UPDATE_ACCESSIBLE_APPS;
	accessibleApps: Api.AspDriveResourceItem[];
}

interface UpdateAuthMethodsAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.UPDATE_AUTH_METHODS;
	orgAuthMethods: AuthMethod[];
}

interface UpdateCredentialsAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.UPDATE_CREDENTIALS;
	credentials: MultiAppAuthenticationCredentials;
}

interface UpdateServerAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.UPDATE_SERVER;
	server: Server;
}

interface SetFormValueAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.SET_FORM_VALUE;
	input: string;
	value: string;
}

interface SetSessionCheckingAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.SET_SESSION_CHECKING;
}

interface ToggleCheckSessionStatusAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.TOGGLE_CHECK_SESSION_STATUS;
	isCheckingSession: boolean;
}

interface SetAnotherUserLoginAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.SET_ANOTHER_USER_LOGIN;
}

export type MultiAppAuthenticationInfosAction = LoginSuccessfullAction
	| LoginFailedAction
	| LogoutAction
	| UpdateClientAction
	| UpdateAccessibleAppsAction
	| UpdateAuthMethodsAction
	| ResetLoginStatusAction
	| SetNoCompanyStatusAction
	| UpdateCredentialsAction
	| ResetLoginStatusAction
	| UpdateServerAction
	| SetFormValueAction
	| SetSessionCheckingAction
	| ToggleCheckSessionStatusAction
	| SetAnotherUserLoginAction;

interface UpdateMultiAppCredentialsArgs {
	login?: string;
	password?: string;
	company?: string;
	method?: AuthMethod;
	token?: string;
}


export const initializeMultiAppAuthentication = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {

	UserAgentUtils.isOnlinePromise()// needed because isOnline might not be initialized yet
		.then(isOnline => {
			if (!isOnline) {
				const previousGalleryConfig = LocalStorageManager.getStoreGalleryConfig();
				if (previousGalleryConfig) {
					dispatch(initGallery(previousGalleryConfig));
					return;
				}

				dispatch(createNotification('info.offline_login'));
				return;
			}

			dispatch(getServer())
				.then(() => {
					const { credentials } = getState().multiAppAuthenticationInfos;

					if (credentials) {
						dispatch(signIn(credentials, true));
					}
				});
		})
	;
};

export function storeCredentials(credentials: MultiAppAuthenticationCredentials): void {
	LocalStorageManager.setMultiAppCredentials(credentials);
}

export const updateCredentials = (updateCredentialsArgs: UpdateMultiAppCredentialsArgs) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { credentials } = getState().multiAppAuthenticationInfos;
	let newCredentialsState = credentials ? credentials : initialCredentials;

	newCredentialsState.login = updateCredentialsArgs.login !== undefined
		? updateCredentialsArgs.login
		: newCredentialsState.login;
	newCredentialsState.password = updateCredentialsArgs.password !== undefined
		? updateCredentialsArgs.password
		: newCredentialsState.password;
	newCredentialsState.company = updateCredentialsArgs.company !== undefined
		? updateCredentialsArgs.company
		: newCredentialsState.company;
	newCredentialsState.method = updateCredentialsArgs.method !== undefined
		? updateCredentialsArgs.method
		: newCredentialsState.method;
	newCredentialsState.token = updateCredentialsArgs.token !== undefined
		? updateCredentialsArgs.token
		: newCredentialsState.token;

	dispatch({
		type: MultiAppAuthenticationInfosActionKeys.UPDATE_CREDENTIALS,
		credentials: newCredentialsState
	});
};

export const getAuthMethods = (company: string) => (dispatch: Dispatch<StoreState>): Promise<void> => {
	return ApiUtils.getOrgAuthMethods(company)
		.then(response => {
			const { orgAuthMethods } = response;
			if (orgAuthMethods) {
				dispatch(updateCredentials({ company }));
				dispatch({ type: MultiAppAuthenticationInfosActionKeys.UPDATE_AUTH_METHODS, orgAuthMethods });
				return Promise.resolve();
			} else {
				return Promise.reject(AspConnexionStatus.NO_COMPANY_ERROR);
			}
		})
		.catch(() => Promise.reject(AspConnexionStatus.NO_COMPANY_ERROR));
};

export const signIn = (credentials: MultiAppAuthenticationCredentials, shouldSaveCredentials: boolean) => (dispatch: Dispatch<StoreState>): Promise<void> => {
	if (!shouldSaveCredentials) {
		LocalStorageManager.removeMultiAppCredentials();
	}

	return ApiUtils.loginToAsp(credentials)
		.catch(() => {
			LocalStorageManager.removeMultiAppCredentials();
			return Promise.reject(AspConnexionStatus.AUTH_ERROR);
		})
		.then(response => {
			const { token, login, associatedApps, clientId, oneSessPerUser } = response;
			const resolvedCredentials = credentials.method === AuthMethod.Webpublication
				? { ...credentials, token }
				: { ...credentials, login };

			if (shouldSaveCredentials) {
				LocalStorageManager.setMultiAppCredentials(resolvedCredentials);
			}

			dispatch(updateCredentials(resolvedCredentials));

			if (oneSessPerUser) {
				dispatch({ type: MultiAppAuthenticationInfosActionKeys.SET_SESSION_CHECKING });
			}

			dispatch({ type: MultiAppAuthenticationInfosActionKeys.UPDATE_CLIENT, clientId });
			dispatch({ type: MultiAppAuthenticationInfosActionKeys.UPDATE_ACCESSIBLE_APPS, accessibleApps: associatedApps });

			if (associatedApps.length === 1) {
				const { clientId, globalId } = associatedApps[0];
				dispatch(loadGallery(clientId, globalId, shouldSaveCredentials));
			}
			return associatedApps.length > 0 ? Promise.resolve() : Promise.reject(AspConnexionStatus.NO_APP_ERROR);
		})
		.catch(reason => {
			dispatch({ type: MultiAppAuthenticationInfosActionKeys.LOGIN_ERROR, status: reason });
			return Promise.reject(reason);
		});
};

export const signOut = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		await Promise.all([
			ApiUtils.logoutFromAsp(),
			ApiUtils.logoutFromApplication(),
		]);
	} catch (error) {
		if (!UserAgentUtils.isMobileApp) {
			// reload redirects to external login page in web version in case getting error from server (e.g., another user login)
			window.location.reload();
		}
	} finally {
		if (UserAgentUtils.isMobileApp) {
			LocalStorageManager.removeMultiAppCredentials();
			LocalStorageManager.removeStoreGalleryConfig();
			dispatch(logout());
		} else {
			LocalStorageManager.removeUsername();
			LocalStorageManager.removePassword();
			const { galleryInfos: { config } } = getState();
			// call app url to redirect to external login page in web version
			const appUrl = UrlUtils.getApplicationUrl(config);
			await Utils.promiseXHR('GET', appUrl);
		}
	}
};

const logout = (): LogoutAction => ({
	type: MultiAppAuthenticationInfosActionKeys.LOGOUT,
});

export const loadGallery = (clientId: number, galleryId: number, shouldSaveConfig: boolean = false) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
		return ApiUtils.getConfig(clientId, galleryId)
			.then(config => dispatch(initGallery(config, shouldSaveConfig)));
	}
;

export const initGallery = (config: Api.Config, shouldSaveConfig: boolean = false) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { credentials } = getState().multiAppAuthenticationInfos;
	const login = credentials && credentials.login ? credentials.login : '';

	LocalStorageManager.setGalleryPrefix(config.applicationGallery, login);
	dispatch(updateMultiAppConfig(config, shouldSaveConfig));
	dispatch({ type: MultiAppAuthenticationInfosActionKeys.LOGIN_SUCCESS });
};

export const updateMultiAppConfig = (data: Api.Config, shouldSave: boolean) =>
	(dispatch: Dispatch<StoreState>): void => {

		dispatch(receiveConfig(data));
		if (shouldSave) {
			LocalStorageManager.setStoreGalleryConfig(data);
		}
	}
;

export const getServer = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const { server } = getState().multiAppAuthenticationInfos;

	if (server) {
		return Promise.resolve();
	}

	return ApiUtils.getAvailableServers()
		.then(servers => {
			let closestServerKey: string = '';
			const serverKeys = Object.keys(servers);

			for (let i = 0; i < serverKeys.length; i++) {
				const key = serverKeys[i];
				if (servers[key].closestServer) {
					closestServerKey = key;
					break;
				}
			}
			LocalStorageManager.setMultiAppServer(closestServerKey as Server);
			dispatch({ type: MultiAppAuthenticationInfosActionKeys.UPDATE_SERVER, server: closestServerKey });
		});
};

export const setFormValue = (input: string, value: string) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	dispatch({
		type: MultiAppAuthenticationInfosActionKeys.SET_FORM_VALUE,
		input,
		value
	});
};

const toggleCheckSessionStatus = (isCheckingSession: boolean): ToggleCheckSessionStatusAction => ({
	type: MultiAppAuthenticationInfosActionKeys.TOGGLE_CHECK_SESSION_STATUS,
	isCheckingSession,
});

const setAnotherUserLogin = (): SetAnotherUserLoginAction => ({
	type: MultiAppAuthenticationInfosActionKeys.SET_ANOTHER_USER_LOGIN,
});

export const checkSession = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const CHECK_SESSION_DELAY = 5000;
	const {
		multiAppAuthenticationInfos: { isCheckingSession },
	} = getState();

	// to prevent unnecessary calls to api in case several actions are dispatched frequently
	// (for example, typing in forms)
	if (isCheckingSession) {
		return;
	}

	try {
		dispatch(toggleCheckSessionStatus(true));
		await ApiUtils.checkSession();
		setTimeout(() => {
			dispatch(toggleCheckSessionStatus(false));
		}, CHECK_SESSION_DELAY);
	} catch (error) {
		if (error.status === ServerStatusCode.CONFLICT) {
			dispatch(setAnotherUserLogin());
		}
	}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/MultiAppAuthentication/actions.ts